import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  TrendingUp, TrendingDown, DollarSign, Users, CheckCircle,
  ArrowRight, Activity, Heart, BarChart3, Building2,
  Target, Clock, Calendar, FileText, Award, ChevronRight
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, LineChart, Line, Legend } from 'recharts';

const beforeAfter = {
  claimDenials: { before: 22.4, after: 8.1, unit: '%', label: 'Claim Denial Rate' },
  turnaround: { before: 45, after: 12, unit: ' days', label: 'Avg Claims Turnaround' },
  patientSatisfaction: { before: 72, after: 94, unit: '%', label: 'Patient Satisfaction' },
  noShow: { before: 18, after: 6.5, unit: '%', label: 'No-Show Rate' },
  revenuePerVisit: { before: 185, after: 268, unit: '', label: 'Revenue per Visit ($)' },
  readmissionRate: { before: 14.2, after: 7.8, unit: '%', label: '30-Day Readmission Rate' },
};

const timeline = [
  { month: 'Month 1', event: 'Platform Deployment', detail: 'Savenes deployed across 3 departments. Initial EHR integration with Epic via FHIR.' },
  { month: 'Month 2', event: 'AI CDS Activation', detail: 'Clinical Decision Support enabled. Drug interaction alerts caught 47 potential adverse events.' },
  { month: 'Month 3', event: 'Claims Automation', detail: 'Automated pre-submission checks reduced coding errors by 65%. Clean claim rate jumped to 96%.' },
  { month: 'Month 4', event: 'Population Health Launch', detail: 'Risk stratification identified 340 high-risk patients. Care management programs enrolled 89% within 2 weeks.' },
  { month: 'Month 5', event: 'Patient Portal Live', detail: 'Self-service portal reduced front desk calls by 40%. Online scheduling adoption reached 62%.' },
  { month: 'Month 6', event: 'CQM Dashboard Active', detail: 'HEDIS/MIPS quality scores tracked in real-time. MIPS composite score increased from 48 to 76.' },
];

const revenueData = [
  { month: 'Oct', before: 2100000, after: 2100000 },
  { month: 'Nov', before: 2150000, after: 2280000 },
  { month: 'Dec', before: 2080000, after: 2410000 },
  { month: 'Jan', before: 2200000, after: 2580000 },
  { month: 'Feb', before: 2180000, after: 2720000 },
  { month: 'Mar', before: 2250000, after: 2890000 },
];

const qualityData = [
  { month: 'Oct', mips: 48, hedis: 62, satisfaction: 72 },
  { month: 'Nov', mips: 52, hedis: 65, satisfaction: 78 },
  { month: 'Dec', mips: 58, hedis: 70, satisfaction: 82 },
  { month: 'Jan', mips: 64, hedis: 74, satisfaction: 87 },
  { month: 'Feb', mips: 71, hedis: 79, satisfaction: 91 },
  { month: 'Mar', mips: 76, hedis: 83, satisfaction: 94 },
];

const roiBreakdown = [
  { category: 'Reduced Denials', savings: 420000 },
  { category: 'Faster Collections', savings: 310000 },
  { category: 'Prevented Readmissions', savings: 280000 },
  { category: 'Reduced No-Shows', savings: 195000 },
  { category: 'Staff Efficiency', savings: 165000 },
  { category: 'Quality Bonuses (MIPS)', savings: 125000 },
];

export default function CaseStudyPage() {
  const [activeSection, setActiveSection] = useState('overview');
  const totalSavings = roiBreakdown.reduce((sum, r) => sum + r.savings, 0);

  return (
    <div className="space-y-6" data-testid="case-study-page">
      <div className="bg-gradient-to-r from-[#0A2342] to-[#0E7490] rounded-2xl p-8 text-white">
        <div className="flex items-center gap-2 text-white/60 text-sm mb-2">
          <FileText className="w-4 h-4" /> Case Study
        </div>
        <h1 className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Meridian General Hospital
        </h1>
        <p className="text-white/80 mt-2 max-w-2xl">
          How a 320-bed community hospital transformed operations, reduced claim denials by 64%, 
          and generated $1.5M in annual savings using Savenes's Healthcare Intelligence Platform.
        </p>
        <div className="flex flex-wrap gap-6 mt-6">
          <div>
            <div className="text-3xl font-bold">${(totalSavings / 1000000).toFixed(1)}M</div>
            <div className="text-sm text-white/60">Annual Savings</div>
          </div>
          <div className="w-px bg-white/20" />
          <div>
            <div className="text-3xl font-bold">64%</div>
            <div className="text-sm text-white/60">Denial Reduction</div>
          </div>
          <div className="w-px bg-white/20" />
          <div>
            <div className="text-3xl font-bold">94%</div>
            <div className="text-sm text-white/60">Patient Satisfaction</div>
          </div>
          <div className="w-px bg-white/20" />
          <div>
            <div className="text-3xl font-bold">76</div>
            <div className="text-sm text-white/60">MIPS Score</div>
          </div>
        </div>
      </div>

      {/* Hospital Profile */}
      <Card className="careiq-card">
        <CardContent className="p-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-3 bg-[#F8FAFC] rounded-lg">
              <div className="text-xs text-[#64748B]">Facility Type</div>
              <div className="font-medium text-[#0A2342] mt-1">Community Hospital</div>
            </div>
            <div className="p-3 bg-[#F8FAFC] rounded-lg">
              <div className="text-xs text-[#64748B]">Bed Count</div>
              <div className="font-medium text-[#0A2342] mt-1">320 beds</div>
            </div>
            <div className="p-3 bg-[#F8FAFC] rounded-lg">
              <div className="text-xs text-[#64748B]">Annual Patient Volume</div>
              <div className="font-medium text-[#0A2342] mt-1">48,000+ visits/year</div>
            </div>
            <div className="p-3 bg-[#F8FAFC] rounded-lg">
              <div className="text-xs text-[#64748B]">EHR System</div>
              <div className="font-medium text-[#0A2342] mt-1">Epic (FHIR R4)</div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="results" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4 max-w-lg">
          <TabsTrigger value="results">Results</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="roi">ROI Analysis</TabsTrigger>
          <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
        </TabsList>

        {/* Results Tab */}
        <TabsContent value="results" className="space-y-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(beforeAfter).map(([key, val]) => {
              const improved = key === 'revenuePerVisit' || key === 'patientSatisfaction' ? val.after > val.before : val.after < val.before;
              const pctChange = ((val.after - val.before) / val.before * 100).toFixed(0);
              return (
                <Card key={key} className="careiq-card" data-testid={`metric-${key}`}>
                  <CardContent className="p-4">
                    <div className="text-xs text-[#64748B]">{val.label}</div>
                    <div className="flex items-end gap-3 mt-2">
                      <div>
                        <div className="text-xs text-[#94A3B8] line-through">{val.before}{val.unit}</div>
                        <div className="text-2xl font-bold text-[#0A2342]">{val.after}{val.unit}</div>
                      </div>
                      <div className={`flex items-center gap-1 text-sm font-medium ${improved ? 'text-[#166534]' : 'text-[#991B1B]'}`}>
                        {improved ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        {pctChange > 0 ? '+' : ''}{pctChange}%
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Revenue Comparison Chart */}
          <Card className="careiq-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#0A2342]">Monthly Revenue: Before vs After Savenes</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={revenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                  <YAxis stroke="#64748B" fontSize={12} tickFormatter={v => `$${(v/1000000).toFixed(1)}M`} />
                  <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, '']} />
                  <Legend />
                  <Area type="monotone" dataKey="before" name="Before Savenes" stroke="#94A3B8" fill="#94A3B8" fillOpacity={0.1} strokeWidth={2} strokeDasharray="5 5" />
                  <Area type="monotone" dataKey="after" name="After Savenes" stroke="#0E7490" fill="#0E7490" fillOpacity={0.15} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Quality Improvement Chart */}
          <Card className="careiq-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#0A2342]">Quality Score Improvement Over Time</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={qualityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#64748B" fontSize={12} />
                  <YAxis domain={[40, 100]} stroke="#64748B" fontSize={12} />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="mips" name="MIPS Score" stroke="#0E7490" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="hedis" name="HEDIS Compliance" stroke="#166534" strokeWidth={2} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="satisfaction" name="Patient Satisfaction" stroke="#7C3AED" strokeWidth={2} dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline">
          <Card className="careiq-card">
            <CardContent className="p-6">
              <div className="space-y-0">
                {timeline.map((item, i) => (
                  <div key={i} className="flex gap-4" data-testid={`timeline-${i}`}>
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full bg-[#0E7490] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{i + 1}</div>
                      {i < timeline.length - 1 && <div className="w-0.5 h-full bg-[#E2E8F0] min-h-[40px]" />}
                    </div>
                    <div className="pb-8">
                      <div className="text-xs text-[#0E7490] font-medium">{item.month}</div>
                      <div className="font-semibold text-[#0A2342] mt-0.5">{item.event}</div>
                      <p className="text-sm text-[#64748B] mt-1">{item.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ROI Tab */}
        <TabsContent value="roi" className="space-y-6">
          <Card className="careiq-card bg-gradient-to-r from-[#0A2342] to-[#0E7490] text-white">
            <CardContent className="p-6 flex items-center justify-between">
              <div>
                <div className="text-sm opacity-80">Total Annual Savings</div>
                <div className="text-4xl font-bold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>${totalSavings.toLocaleString()}</div>
                <div className="text-sm opacity-80 mt-1">385% return on investment in Year 1</div>
              </div>
              <Award className="w-16 h-16 opacity-30" />
            </CardContent>
          </Card>

          <Card className="careiq-card">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#0A2342]">Savings by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={roiBreakdown} layout="vertical" barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" stroke="#64748B" fontSize={12} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="category" stroke="#64748B" fontSize={11} width={140} />
                  <Tooltip formatter={(v) => [`$${v.toLocaleString()}`, 'Savings']} />
                  <Bar dataKey="savings" fill="#0E7490" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid sm:grid-cols-3 gap-4">
            <Card className="careiq-card">
              <CardContent className="p-4 text-center">
                <div className="text-xs text-[#64748B]">Implementation Cost</div>
                <div className="text-xl font-bold text-[#0A2342] mt-1">$385,000</div>
                <div className="text-xs text-[#64748B]">One-time + Year 1 license</div>
              </CardContent>
            </Card>
            <Card className="careiq-card">
              <CardContent className="p-4 text-center">
                <div className="text-xs text-[#64748B]">Payback Period</div>
                <div className="text-xl font-bold text-[#166534] mt-1">3.1 months</div>
                <div className="text-xs text-[#64748B]">Breakeven achieved quickly</div>
              </CardContent>
            </Card>
            <Card className="careiq-card">
              <CardContent className="p-4 text-center">
                <div className="text-xs text-[#64748B]">5-Year NPV</div>
                <div className="text-xl font-bold text-[#0E7490] mt-1">$6.2M</div>
                <div className="text-xs text-[#64748B]">Net present value</div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Testimonials Tab */}
        <TabsContent value="testimonials">
          <div className="grid sm:grid-cols-2 gap-6">
            <Card className="careiq-card">
              <CardContent className="p-6">
                <div className="text-[#0E7490] text-4xl font-serif mb-3">"</div>
                <p className="text-[#334155] text-sm leading-relaxed">
                  Savenes transformed how we manage our revenue cycle. The AI-powered pre-submission 
                  checks alone saved us from hundreds of preventable denials. Our clean claim rate went 
                  from 78% to 96% in three months.
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0E7490] rounded-full flex items-center justify-center text-white font-bold">RM</div>
                  <div>
                    <div className="font-medium text-[#0A2342]">Rebecca Martinez, MBA</div>
                    <div className="text-xs text-[#64748B]">Chief Financial Officer</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="careiq-card">
              <CardContent className="p-6">
                <div className="text-[#0E7490] text-4xl font-serif mb-3">"</div>
                <p className="text-[#334155] text-sm leading-relaxed">
                  The Clinical Decision Support has been a game-changer. The drug interaction alerts 
                  and evidence-based care recommendations help my team make faster, more confident 
                  clinical decisions. Patient outcomes have measurably improved.
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#1A56DB] rounded-full flex items-center justify-center text-white font-bold">JC</div>
                  <div>
                    <div className="font-medium text-[#0A2342]">Dr. James Chen, MD, FACP</div>
                    <div className="text-xs text-[#64748B]">Chief Medical Officer</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="careiq-card">
              <CardContent className="p-6">
                <div className="text-[#0E7490] text-4xl font-serif mb-3">"</div>
                <p className="text-[#334155] text-sm leading-relaxed">
                  Our patients love the self-service portal. Online scheduling, secure messaging, 
                  and lab result access have dramatically reduced phone call volume. We reallocated 
                  3 FTEs from phone duty to direct patient care.
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#166534] rounded-full flex items-center justify-center text-white font-bold">AL</div>
                  <div>
                    <div className="font-medium text-[#0A2342]">Angela Lewis, RN, BSN</div>
                    <div className="text-xs text-[#64748B]">Director of Patient Experience</div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="careiq-card">
              <CardContent className="p-6">
                <div className="text-[#0E7490] text-4xl font-serif mb-3">"</div>
                <p className="text-[#334155] text-sm leading-relaxed">
                  The MIPS dashboard alone paid for the platform. We went from a negative payment 
                  adjustment to earning a 76-point composite score with positive reimbursement impact. 
                  Savenes made quality reporting effortless.
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#7C3AED] rounded-full flex items-center justify-center text-white font-bold">DP</div>
                  <div>
                    <div className="font-medium text-[#0A2342]">David Park</div>
                    <div className="text-xs text-[#64748B]">Quality Improvement Director</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
